import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Moon, Sun, User, Stethoscope, ChevronDown, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import BrandRingLogo, { DEFAULT_LOGO_HEIGHT } from "./BrandRingLogo";
import SiteBanners from "./SiteBanners";
import { useConsultEnabled } from "../lib/useConsultEnabled";
import { useGlobals, usePublishedPages } from "../site/useGlobals";
import { space } from "../site/spacing";

const FALLBACK_MENU = [
    { id: "m_home", label: "Home", href: "/", visible: true },
    { id: "m_shop", label: "Shop", href: "/shop", visible: true },
    { id: "m_consult", label: "Consultation", href: "/consultation", visible: true, consult_only: true },
    { id: "m_science", label: "Science", href: "/science", visible: true },
    { id: "m_blog", label: "Blog", href: "/blog", visible: true },
    { id: "m_about", label: "About Us", href: "/about-us", visible: true },
    { id: "m_trust", label: "Why Trust Us?", href: "/why-trust-us", visible: true },
];

const linkClass = ({ isActive }) =>
    `mono text-[11px] tracking-[0.16em] whitespace-nowrap link-sweep transition-colors ${isActive ? "text-gold is-active" : "text-zinc-400 hover:text-white"}`;

export default function Navbar() {
    const { count, setOpen } = useCart();
    const { theme, toggle, canSwitch } = useTheme();
    const { customer } = useCustomerAuth();
    const { showLink: consultOn } = useConsultEnabled();
    const { header, announcement } = useGlobals();
    const pages = usePublishedPages();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [expanded, setExpanded] = React.useState(null);
    const [openMenu, setOpenMenu] = React.useState(null);
    const [openSub, setOpenSub] = React.useState(null);
    const [scrolled, setScrolled] = React.useState(false);
    const loc = useLocation();

    const extraPages = pages
        .filter((p) => p.show_in_menu && ["header", "both"].includes(p.menu_location || "header"))
        .map((p) => ({ id: `page_${p.slug}`, label: p.name, href: `/${p.slug}`, visible: true, children: [] }));
    const menu = [...(header.menu || FALLBACK_MENU), ...extraPages]
        .filter((m) => m.visible !== false && (!m.consult_only || consultOn));
    const announce = announcement.enabled ? announcement : null;

    React.useEffect(() => { setMobileOpen(false); setOpenMenu(null); setOpenSub(null); }, [loc.pathname]);
    React.useEffect(() => {
        document.body.classList.toggle("announce-on", !!announce);
        return () => document.body.classList.remove("announce-on");
    }, [announce]);
    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header className="fixed top-0 inset-x-0 z-40">
            {announce && (
                <div data-testid="announcement-bar"
                    className={`h-9 flex items-center justify-center gap-3 px-4 text-center mono text-[10px] tracking-[0.18em] ${
                        announcement.tone === "lime" ? "bg-lime text-bg0" : "bg-gold text-bg0"}`}>
                    <span>{announcement.text}</span>
                    {announcement.link_label && (
                        <Link to={announcement.link_href || "/"} className="underline" data-testid="announcement-link">
                            {announcement.link_label}
                        </Link>
                    )}
                </div>
            )}
            <SiteBanners placement="top_bar" />
            <div className={`transition-colors duration-500 ${scrolled || announce ? "bg-bg0/85 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"}`}>
                <div className="mx-auto max-w-7xl px-5 md:px-10 flex items-center justify-between gap-4 min-h-[4rem] md:min-h-[5rem]"
                    style={{ paddingTop: space(header.space_top), paddingBottom: space(header.space_bottom) }}>
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
                        {header.logo_url
                            ? <img src={header.logo_url} alt="ReHealthMe" style={{ height: `${header.logo_height || DEFAULT_LOGO_HEIGHT}px` }} className="w-auto" />
                            : <BrandRingLogo height={header.logo_height || DEFAULT_LOGO_HEIGHT} />}
                    </Link>

                    <nav className="hidden xl:flex items-center gap-4 2xl:gap-7 min-w-0">
                        {menu.map((m) => {
                            const kids = (m.children || []).filter((c) => c.visible !== false);
                            if (kids.length === 0) {
                                return (
                                    <NavLink key={m.id} to={m.href} end={m.href === "/"} className={linkClass}
                                        data-testid={`nav-link-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {m.label}
                                    </NavLink>
                                );
                            }
                            return (
                                <div key={m.id} className="relative group" data-testid={`nav-dropdown-${m.id}`}
                                    onMouseLeave={() => setOpenMenu(null)}>
                                    <span className="inline-flex items-center gap-1">
                                        <NavLink to={m.href} className={linkClass}
                                            data-testid={`nav-link-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                            {m.label}
                                        </NavLink>
                                        <button onClick={() => setOpenMenu(openMenu === m.id ? null : m.id)}
                                            data-testid={`nav-dropdown-toggle-${m.id}`} aria-label={`Open ${m.label} menu`}
                                            aria-expanded={openMenu === m.id} className="text-zinc-400 hover:text-gold">
                                            <ChevronDown className={`w-3 h-3 transition-transform ${openMenu === m.id ? "rotate-180 text-gold" : ""}`} />
                                        </button>
                                    </span>
                                    <div className={`absolute left-0 top-full pt-4 transition-opacity group-hover:opacity-100 group-hover:visible ${
                                        openMenu === m.id ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                                        <div className="min-w-52 border border-white/10 bg-bg1/95 backdrop-blur-xl py-2">
                                            {kids.map((c) => {
                                                const grandKids = (c.children || []).filter((g) => g.visible !== false);
                                                if (!grandKids.length) {
                                                    return (
                                                        <Link key={c.id} to={c.href} data-testid={`nav-sublink-${c.id}`}
                                                            className="block px-4 py-2.5 mono text-[11px] tracking-[0.16em] text-zinc-300 hover:text-gold hover:bg-white/5">
                                                            {c.label}
                                                        </Link>
                                                    );
                                                }
                                                return (
                                                    <div key={c.id} className="relative group/sub"
                                                        data-testid={`nav-subdropdown-${c.id}`}>
                                                        <div className="flex items-center justify-between gap-2 pr-2 hover:bg-white/5">
                                                            <Link to={c.href} data-testid={`nav-sublink-${c.id}`}
                                                                className="flex-1 px-4 py-2.5 mono text-[11px] tracking-[0.16em] text-zinc-300 hover:text-gold">
                                                                {c.label}
                                                            </Link>
                                                            <button onClick={() => setOpenSub(openSub === c.id ? null : c.id)}
                                                                data-testid={`nav-subdropdown-toggle-${c.id}`}
                                                                aria-label={`Open ${c.label} menu`} aria-expanded={openSub === c.id}
                                                                className="text-zinc-500 hover:text-gold p-1">
                                                                <ChevronRight className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                        <div className={`absolute left-full top-0 pl-1 transition-opacity group-hover/sub:opacity-100 group-hover/sub:visible ${
                                                            openSub === c.id ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                                                            <div className="min-w-48 border border-white/10 bg-bg1/95 backdrop-blur-xl py-2">
                                                                {grandKids.map((g) => (
                                                                    <Link key={g.id} to={g.href} data-testid={`nav-sublink-${g.id}`}
                                                                        className="block px-4 py-2.5 mono text-[11px] tracking-[0.16em] text-zinc-300 hover:text-gold hover:bg-white/5">
                                                                        {g.label}
                                                                    </Link>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-2 shrink-0">
                        {header.show_account !== false && (
                            <Link to={customer ? "/account" : "/account/login"} data-testid="nav-account-link"
                                title={customer ? "My account" : "Sign in"}
                                className="p-2.5 border border-white/15 hover:border-gold hover:text-gold transition-colors">
                                <User className="w-4 h-4" />
                            </Link>
                        )}
                        {canSwitch && header.show_theme !== false && (
                            <button onClick={toggle} data-testid="nav-theme-toggle" aria-label="Toggle theme"
                                title={theme === "dark" ? "Switch to white theme" : "Switch to dark theme"}
                                className="p-2.5 border border-white/15 hover:border-gold hover:text-gold transition-colors">
                                {theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                            </button>
                        )}
                        {consultOn && header.show_consult !== false && (
                            <Link to={header.consult_href || "/consultation"} data-testid="nav-consultation-cta"
                                className="hidden xl:inline-flex items-center gap-2 px-4 py-2.5 border border-gold/50 text-gold hover:bg-gold hover:text-bg0 mono text-[11px] tracking-[0.18em] transition-colors">
                                <Stethoscope className="w-4 h-4" />
                                <span>{header.consult_label || "Consult"}</span>
                            </Link>
                        )}
                        {header.show_cart !== false && (
                            <button onClick={() => setOpen(true)} data-testid="nav-cart-button"
                                className="relative inline-flex items-center gap-2 px-3 md:px-4 py-2.5 border border-white/15 hover:border-gold hover:text-gold mono text-[11px] tracking-[0.18em] transition-colors">
                                <ShoppingBag className="w-4 h-4" />
                                <span className="hidden sm:inline">Cart</span>
                                <span data-testid="nav-cart-count"
                                    className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-gold text-bg0 text-[10px] font-bold">
                                    {count}
                                </span>
                            </button>
                        )}
                        {header.show_cta !== false && (
                            <Link to={header.cta_href || "/product/redrive-max"} data-testid="nav-buy-now-button"
                                className="!hidden 2xl:!inline-flex btn-pill btn-primary !py-2.5 !px-5">
                                {header.cta_label || "Buy Now"}
                            </Link>
                        )}
                        <button className="xl:hidden p-2.5 border border-white/15" onClick={() => setMobileOpen((v) => !v)}
                            data-testid="nav-mobile-toggle" aria-label="Menu">
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {mobileOpen && (
                <div className="xl:hidden mx-4 mt-2 border border-white/10 bg-bg1/95 backdrop-blur-xl p-3 max-h-[75vh] overflow-y-auto"
                    data-testid="nav-mobile-panel">
                    <div className="flex flex-col">
                        {menu.map((m) => {
                            const kids = (m.children || []).filter((c) => c.visible !== false);
                            const open = expanded === m.id;
                            const key = m.label.toLowerCase().replace(/\s+/g, "-");
                            return (
                                <div key={m.id} className="border-b border-white/8">
                                    <div className="flex items-center">
                                        <NavLink to={m.href} end={m.href === "/"}
                                            data-testid={`nav-mobile-link-${key}`}
                                            className={({ isActive }) => `flex-1 py-3.5 px-3 mono text-[11px] tracking-[0.2em] ${isActive ? "text-gold" : "text-zinc-300"}`}>
                                            {m.label}
                                        </NavLink>
                                        {!!kids.length && (
                                            <button onClick={() => setExpanded(open ? null : m.id)}
                                                data-testid={`nav-mobile-expand-${key}`} aria-label={`Expand ${m.label}`}
                                                className="p-3 text-zinc-500">
                                                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180 text-gold" : ""}`} />
                                            </button>
                                        )}
                                    </div>
                                    {open && !!kids.length && (
                                        <div className="pb-2 pl-3">
                                            {kids.map((c) => (
                                                <div key={c.id}>
                                                    <Link to={c.href} data-testid={`nav-mobile-sublink-${c.id}`}
                                                        className="block py-2.5 px-3 mono text-[10px] tracking-[0.18em] text-zinc-400 hover:text-gold">
                                                        {c.label}
                                                    </Link>
                                                    {(c.children || []).filter((g) => g.visible !== false).map((g) => (
                                                        <Link key={g.id} to={g.href} data-testid={`nav-mobile-sublink-${g.id}`}
                                                            className="block py-2 pl-8 pr-3 mono text-[10px] tracking-[0.16em] text-zinc-500 hover:text-gold">
                                                            {g.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {consultOn && header.show_consult !== false && (
                        <Link to={header.consult_href || "/consultation"} data-testid="nav-mobile-consult"
                            className="mt-3 flex items-center justify-center gap-2 py-3 border border-gold/50 text-gold mono text-[11px] tracking-[0.18em]">
                            <Stethoscope className="w-4 h-4" /> {header.consult_label || "Consult"}
                        </Link>
                    )}
                </div>
            )}
        </header>
    );
}
